/**
 * Panel "Recenzje" i okno "Link do recenzji": czyste funkcje (teksty, daty, filtry, walidacja), bez DOM i bez sieci.
 *
 * Kontrakt danych: shared/recenzjeTypy.ts. Ten plik nie importuje lib/api.ts (api.ts importuje stad nazwe pliku CSV),
 * wiec zaleznosc idzie w jedna strone.
 *
 * Daty liczymy w czasie lokalnym przegladarki, bez Intl: wynik jest ten sam w kazdej przegladarce i da sie go
 * przetestowac bez zgadywania strefy czasowej.
 */

import {
  ETYKIETY_WERDYKTOW,
  KOD_ROZMOWY_KONTA_UZYTKOWNIKA,
  MAX_ROZMOW_W_LINKU,
  MAX_ZNAKOW_ETYKIETY,
  POLE_ROZMOW_KONT_UZYTKOWNIKOW,
  sciezkaLinku,
  type LinkRecenzjiAdmin,
  type PoprawkaAdmin,
  type RodzajCeluKorekty,
  type StatusLinku,
  type Werdykt,
} from "@shared/recenzjeTypy.js";
import { odmien } from "./polski.js";

// ---------------------------------------------------------------------------
// Adres linku
// ---------------------------------------------------------------------------

/**
 * Pelny adres linku do wklejenia recenzentowi: origin przegladarki plus sciezka z serwera. Serwer podaje tylko sciezke
 * ("/r/<token>"), bo adresu publicznego aplikacji nie zna. Gdy sciezki brakuje, skladamy ja z tokenu.
 */
export function pelnyAdresLinku(link: Pick<LinkRecenzjiAdmin, "token" | "sciezka">, origin: string): string {
  const sciezka = link.sciezka && link.sciezka.startsWith("/") ? link.sciezka : sciezkaLinku(link.token);
  return `${origin.replace(/\/+$/, "")}${sciezka}`;
}

// ---------------------------------------------------------------------------
// Daty
// ---------------------------------------------------------------------------

const dwie = (n: number): string => String(n).padStart(2, "0");

function dataZIso(iso: string): Date | null {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "08.10.2026" w czasie lokalnym; nieczytelna data to null, nie "Invalid Date". */
export function formatujDate(iso: string): string | null {
  const d = dataZIso(iso);
  return d ? `${dwie(d.getDate())}.${dwie(d.getMonth() + 1)}.${d.getFullYear()}` : null;
}

function godzina(d: Date): string {
  return `${dwie(d.getHours())}:${dwie(d.getMinutes())}`;
}

function tenSamDzien(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** "dzis 14:21", "wczoraj 09:10" albo "05.10.2026 14:21". Zepsuta data to null. */
export function opisMomentu(iso: string, teraz: Date): string | null {
  const d = dataZIso(iso);
  if (!d) return null;
  if (tenSamDzien(d, teraz)) return `dziś ${godzina(d)}`;
  const wczoraj = new Date(teraz.getFullYear(), teraz.getMonth(), teraz.getDate() - 1);
  if (tenSamDzien(d, wczoraj)) return `wczoraj ${godzina(d)}`;
  return `${formatujDate(iso)} ${godzina(d)}`;
}

// ---------------------------------------------------------------------------
// Wiersz linku
// ---------------------------------------------------------------------------

export const ETYKIETY_STATUSU_LINKU: Record<StatusLinku, string> = {
  AKTYWNY: "aktywny",
  WYGASL: "wygasł",
  UNIEWAZNIONY: "unieważniony",
};

/** Wariant plakietki (BD-1): aktywny zielony, wygasly ostrzegawczy, uniewazniony czerwony. Tekst niesie znaczenie. */
export const WARIANTY_STATUSU_LINKU: Record<StatusLinku, "success" | "warning" | "danger"> = {
  AKTYWNY: "success",
  WYGASL: "warning",
  UNIEWAZNIONY: "danger",
};

/** "2 rozmowy", "1 rozmowa", "5 rozmow" (liczba z odmiana). */
export function liczbaRozmow(n: number): string {
  return `${n} ${odmien(n, "rozmowa", "rozmowy", "rozmów")}`;
}

/**
 * Linia pod nazwa linku: "2 rozmowy - bez terminu - ostatnio otwarty dzis 14:21". Uniewazniony link pokazuje date
 * uniewaznienia zamiast ostatniego otwarcia (po uniewaznieniu otwarcia nie maja znaczenia).
 */
export function metaLinku(link: LinkRecenzjiAdmin, teraz: Date): string {
  const czesci: string[] = [liczbaRozmow(link.rozmowy.length)];

  if (link.status === "UNIEWAZNIONY") {
    const kiedy = link.uniewazniony ? formatujDate(link.uniewazniony) : null;
    czesci.push(kiedy ? `unieważniony ${kiedy}` : "unieważniony");
    return czesci.join(" - ");
  }

  const doKiedy = link.waznyDo ? formatujDate(link.waznyDo) : null;
  if (link.waznyDo === null) czesci.push("bez terminu");
  else if (doKiedy) czesci.push(link.status === "WYGASL" ? `wygasł ${doKiedy}` : `do ${doKiedy}`);

  const otwarcie = link.ostatnieOtwarcie ? opisMomentu(link.ostatnieOtwarcie, teraz) : null;
  czesci.push(otwarcie ? `ostatnio otwarty ${otwarcie}` : "jeszcze nie otwarty");
  return czesci.join(" - ");
}

export interface PostepLinku {
  procent: number;
  /** Tekst paska: "17 z 41 wskazan ocenionych". */
  etykieta: string;
}

/** Postep po wszystkich rozmowach linku; null, gdy nie ma czego oceniac (nie pokazujemy paska z "0 z 0"). */
export function postepLinku(postep: LinkRecenzjiAdmin["postep"]): PostepLinku | null {
  if (postep.wskazan <= 0) return null;
  const ocenionych = Math.min(Math.max(postep.ocenionych, 0), postep.wskazan);
  return {
    procent: Math.round((ocenionych / postep.wskazan) * 100),
    etykieta: `${ocenionych} z ${postep.wskazan} ${postep.wskazan === 1 ? "wskazania ocenionego" : "wskazań ocenionych"}`,
  };
}

// ---------------------------------------------------------------------------
// Wiersz poprawki
// ---------------------------------------------------------------------------

/** Slowa werdyktu w liscie administratora (trzecia osoba: to opis cudzej decyzji, nie jego wlasne zdanie). */
export const SLOWA_WERDYKTU: Record<Werdykt, string> = {
  ZGADZAM_SIE: "zgadza się",
  CZESCIOWO: "częściowo",
  NIE_ZGADZAM_SIE: "nie zgadza się",
};

/**
 * Werdykt to stanowisko recenzenta wobec wskazania, nie ocena rozmowy, wiec zostaje na kolorach statusow. Zloto i szarosc
 * oceny (mocna strona, do poprawy) obowiazuja w widokach oceny, a panel Recenzje nim nie jest.
 */
export const WARIANTY_WERDYKTU: Record<Werdykt, "success" | "warning" | "danger"> = {
  ZGADZAM_SIE: "success",
  CZESCIOWO: "warning",
  NIE_ZGADZAM_SIE: "danger",
};

/** Etykiety wartosci filtra "Werdykt". Slowa pierwszej osoby, takie same jak w widoku recenzenta. */
export const OPCJE_FILTRA_WERDYKTU: readonly { wartosc: "" | Werdykt; etykieta: string }[] = [
  { wartosc: "", etykieta: "Wszystkie" },
  { wartosc: "ZGADZAM_SIE", etykieta: ETYKIETY_WERDYKTOW.ZGADZAM_SIE },
  { wartosc: "CZESCIOWO", etykieta: ETYKIETY_WERDYKTOW.CZESCIOWO },
  { wartosc: "NIE_ZGADZAM_SIE", etykieta: ETYKIETY_WERDYKTOW.NIE_ZGADZAM_SIE },
];

/**
 * Rodzaj korekty inny niz zwykle wskazanie dostaje wlasna plakietke. Zwykle wskazanie (FINDING) jej nie ma.
 * MISSED_FINDING to uwaga recenzenta dopisana przy wypowiedzi (w kodzie i API "pominiete"): czlowiek widzi ja jako
 * "Uwaga do wypowiedzi", a rekord bez numeru wypowiedzi (kontrakt go dopuszcza) jako "Uwaga do rozmowy".
 */
export function etykietaRodzajuPoprawki(rodzaj: RodzajCeluKorekty, numerWypowiedzi: number | null = null): string | null {
  switch (rodzaj) {
    case "MISSED_FINDING":
      return numerWypowiedzi === null ? "Uwaga do rozmowy" : "Uwaga do wypowiedzi";
    case "RULE_NOTE":
      return "Notatka know-how";
    case "SESSION":
      return "Cała rozmowa";
    default:
      return null;
  }
}

/** Tytul wiersza: "2026-07-30 - wypowiedz 48" albo sama rozmowa, gdy poprawka nie dotyczy konkretnej wypowiedzi. */
export function tytulPoprawki(p: Pick<PoprawkaAdmin, "rozmowaTytul" | "numerWypowiedzi">): string {
  return p.numerWypowiedzi === null ? p.rozmowaTytul : `${p.rozmowaTytul} - wypowiedź ${p.numerWypowiedzi}`;
}

/** Linia na dole wiersza: "Bez komentarza - Kazimierz Nalepa - dzis 14:20 - metodyka 2026-10-02.1". */
export function metaPoprawki(p: PoprawkaAdmin, teraz: Date): string {
  const czesci: string[] = [];
  if (!p.komentarz && !p.wariant) czesci.push("Bez komentarza");
  if (p.linkEtykieta) czesci.push(p.linkEtykieta);
  const kiedy = opisMomentu(p.zmieniona, teraz);
  if (kiedy) czesci.push(kiedy);
  if (p.wersjaMetodyki) czesci.push(`metodyka ${p.wersjaMetodyki}`);
  return czesci.join(" - ");
}

export interface FiltrPoprawekWidoku {
  /** Id rozmowy albo "" (wszystkie). */
  rozmowa: string;
  /** Werdykt albo "" (wszystkie). */
  werdykt: "" | Werdykt;
}

export const BEZ_FILTROW: FiltrPoprawekWidoku = { rozmowa: "", werdykt: "" };

export function czyFiltrAktywny(f: FiltrPoprawekWidoku): boolean {
  return f.rozmowa !== "" || f.werdykt !== "";
}

export function filtrujPoprawki(lista: readonly PoprawkaAdmin[], f: FiltrPoprawekWidoku): PoprawkaAdmin[] {
  return lista.filter((p) => (f.rozmowa === "" || p.rozmowaId === f.rozmowa) && (f.werdykt === "" || p.werdykt === f.werdykt));
}

/** Rozmowy, o ktorych sa poprawki, do listy filtra: bez powtorzen, alfabetycznie po tytule. */
export function rozmowyPoprawek(lista: readonly PoprawkaAdmin[]): { wartosc: string; etykieta: string }[] {
  const mapa = new Map<string, string>();
  for (const p of lista) if (!mapa.has(p.rozmowaId)) mapa.set(p.rozmowaId, p.rozmowaTytul);
  return [...mapa.entries()]
    .map(([wartosc, etykieta]) => ({ wartosc, etykieta }))
    .sort((a, b) => a.etykieta.localeCompare(b.etykieta, "pl"));
}

// ---------------------------------------------------------------------------
// Plik CSV
// ---------------------------------------------------------------------------

export const NAZWA_CSV_DOMYSLNA = "poprawki-recenzji.csv";

/** Nazwa pliku z Content-Disposition: bez sciezek i znakow zabronionych, zawsze z rozszerzeniem .csv. */
export function nazwaCsvZNaglowka(naglowek: string | null): string {
  const surowa = naglowek ? (/filename\s*=\s*"?([^";]+)"?/i.exec(naglowek)?.[1] ?? "") : "";
  const koncowka = surowa.split(/[\\/]/).pop() ?? "";
  const czysta = [...koncowka]
    .filter((znak) => znak.charCodeAt(0) >= 0x20 && znak.charCodeAt(0) !== 0x7f && !'<>:"|?*'.includes(znak))
    .join("")
    .trim();
  if (czysta === "" || czysta === "." || czysta === "..") return NAZWA_CSV_DOMYSLNA;
  return czysta.toLowerCase().endsWith(".csv") ? czysta : `${czysta}.csv`;
}

// ---------------------------------------------------------------------------
// Okno "Link do recenzji"
// ---------------------------------------------------------------------------

export type KluczWaznosci = "bez" | "7" | "30";

export const OPCJE_WAZNOSCI: readonly { wartosc: KluczWaznosci; etykieta: string; dni: number | null }[] = [
  { wartosc: "bez", etykieta: "Bez terminu", dni: null },
  { wartosc: "7", etykieta: "7 dni", dni: 7 },
  { wartosc: "30", etykieta: "30 dni", dni: 30 },
];

export function dniWaznosci(klucz: KluczWaznosci): number | null {
  return OPCJE_WAZNOSCI.find((o) => o.wartosc === klucz)?.dni ?? null;
}

/** Rozmowa trafia na liste okna, gdy analiza jest skonczona i ma wypowiedzi (bez nich nie ma czego oceniac). */
export function czyRozmowaDoRecenzji(s: { status: string; utteranceCount: number }): boolean {
  return s.status === "DONE" && s.utteranceCount > 0;
}

/**
 * Rozmowa bez zadnego udanego przebiegu soczewki: recenzent dostalby slepy zaulek ("Ta rozmowa nie ma jeszcze gotowej
 * oceny"), wiec okno jej nie oferuje. Brak pola `maOcene` (starszy serwer) to "nie wiadomo", nie "nie ma": wtedy nic nie
 * blokujemy, a ostatnie slowo ma serwer.
 */
export function czyBezOceny(s: { maOcene?: boolean }): boolean {
  return s.maOcene === false;
}

const KOMUNIKAT_ROZMOW_KONT =
  "Serwer odrzucił wybór: rozmowy kont użytkowników nie mogą trafić do linku recenzenta przed zgodą uczestników.";

/**
 * Id rozmow kont uzytkownikow z odmowy serwera (400 rozmowa_konta_uzytkownika, pole `rozmowyKontUzytkownikow`). Okno
 * zna rozmowy kont uzytkownikow z listy analiz (zKontaUzytkownika); odmowa wskazuje te, o ktorych lista nie wiedziala
 * (starszy serwer, biezaca rozmowa spoza listy), a okno je wylacza. Inny kod albo zly ksztalt to pusta lista.
 */
export function rozmowyKontUzytkownikowZOdmowy(kod: string, szczegoly: Readonly<Record<string, unknown>>): string[] {
  if (kod !== KOD_ROZMOWY_KONTA_UZYTKOWNIKA) return [];
  const ids = szczegoly[POLE_ROZMOW_KONT_UZYTKOWNIKOW];
  return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === "string") : [];
}

/**
 * Komunikat pod lista rozmow, gdy serwer odrzucil sam wybor rozmow: 400 bledne_dane z polem rozmowaIds (rozmowa bez
 * oceny albo juz nieistniejaca) albo 400 rozmowa_konta_uzytkownika. Inny blad (albo inne pole) to null i zostaje ogolny
 * komunikat bledu serwera.
 */
export function komunikatBleduWyboruRozmow(kod: string, szczegoly: Readonly<Record<string, unknown>>): string | null {
  if (kod === KOD_ROZMOWY_KONTA_UZYTKOWNIKA) {
    // Obietnica o liscie tylko wtedy, gdy serwer podal, ktore to rozmowy (okno je wtedy wylacza).
    if (rozmowyKontUzytkownikowZOdmowy(kod, szczegoly).length === 0) return KOMUNIKAT_ROZMOW_KONT;
    return `${KOMUNIKAT_ROZMOW_KONT} Te rozmowy są teraz wyłączone na liście, utwórz link z pozostałych.`;
  }
  if (kod !== "bledne_dane") return null;
  const pola = szczegoly.pola;
  if (!Array.isArray(pola)) return null;
  const dotyczyRozmow = pola.some((p) => {
    const nazwa = typeof p === "object" && p !== null ? (p as { pole?: unknown }).pole : undefined;
    return typeof nazwa === "string" && (nazwa === "rozmowaIds" || nazwa.startsWith("rozmowaIds."));
  });
  return dotyczyRozmow
    ? "Serwer odrzucił wybór: któraś z rozmów nie ma jeszcze oceny albo już jej nie ma. Lista została odświeżona, zaznacz tylko rozmowy z oceną."
    : null;
}

export interface BledyFormularzaLinku {
  etykieta?: string;
  rozmowy?: string;
}

/** Walidacja na miejscu (komunikaty pod polami, nie toast). Zwraca pusty obiekt, gdy wszystko w porzadku. */
export function walidujFormularzLinku(wejscie: { etykieta: string; rozmowaIds: readonly string[] }): BledyFormularzaLinku {
  const bledy: BledyFormularzaLinku = {};
  const etykieta = wejscie.etykieta.trim();
  if (etykieta === "") bledy.etykieta = "Wpisz, dla kogo jest ten link.";
  else if (etykieta.length > MAX_ZNAKOW_ETYKIETY) bledy.etykieta = `Etykieta może mieć najwyżej ${MAX_ZNAKOW_ETYKIETY} znaków.`;

  if (wejscie.rozmowaIds.length === 0) bledy.rozmowy = "Zaznacz co najmniej jedną rozmowę.";
  else if (wejscie.rozmowaIds.length > MAX_ROZMOW_W_LINKU) {
    bledy.rozmowy = `Pod jednym linkiem może być najwyżej ${MAX_ROZMOW_W_LINKU} rozmów.`;
  }
  return bledy;
}

/** Zdanie do banera w kroku "Link gotowy": "Kazdy, kto ma ten link, widzi 2 rozmowy i moze zostawic uwagi. ..." */
export function zdanieOLinku(liczbaRozmowLinku: number): string {
  // Biernik: "1 rozmowe", ale "2 rozmowy" i "5 rozmow" wygladaja tak samo jak mianownik.
  const widzi = liczbaRozmowLinku === 1 ? "1 rozmowę" : liczbaRozmow(liczbaRozmowLinku);
  return `Każdy, kto ma ten link, widzi ${widzi} i może zostawić uwagi. Logowanie nie jest potrzebne.`;
}

// ---------------------------------------------------------------------------
// Bledy API po polsku
// ---------------------------------------------------------------------------

const KOMUNIKATY_BLEDOW: Readonly<Record<string, string>> = {
  siec: "Brak połączenia z serwerem. Sprawdź internet i spróbuj ponownie.",
  unauthorized: "Sesja wygasła. Zaloguj się ponownie.",
  forbidden: "To konto nie ma dostępu do panelu recenzji.",
  bledne_dane: "Serwer odrzucił dane formularza. Sprawdź etykietę i listę rozmów (może którejś już nie ma).",
  [KOD_ROZMOWY_KONTA_UZYTKOWNIKA]: KOMUNIKAT_ROZMOW_KONT,
  nie_ma_takiego_linku: "Nie znaleziono tego linku. Odśwież listę.",
  zla_odpowiedz: "Serwer odpowiedział w nieoczekiwany sposób. Spróbuj ponownie za chwilę.",
  blad_serwera: "Serwer zgłosił błąd. Spróbuj ponownie za chwilę.",
};

/** Komunikat po polsku dla `ApiError.code` albo "siec". Nieznany kod to zdanie ogolne, bez kodu w tresci (S-1). */
export function komunikatBleduRecenzji(kod: string): string {
  return KOMUNIKATY_BLEDOW[kod] ?? "Nie udało się wykonać operacji. Spróbuj ponownie za chwilę.";
}
